/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { convertCelsius, tempUnitSuffix, userToCelsius } from '@/utils/conversions'

import type { Units } from './format'

const BLUE = '#3b82f6'
const GREEN = '#22c55e'
const RED = '#ef4444'

// Native range input styled to a bare thumb (transparent track) so two can overlay one
// shared coloured bar. Tailwind arbitrary variants style the thumb.
const THUMB_BASE =
  'pointer-events-none absolute inset-x-0 top-0 m-0 h-6 w-full cursor-pointer appearance-none bg-transparent ' +
  '[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 ' +
  '[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full ' +
  '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow ' +
  '[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 ' +
  '[&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 ' +
  '[&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow [&::-moz-range-track]:bg-transparent'
const THUMB_BLUE = '[&::-webkit-slider-thumb]:bg-blue-500 [&::-moz-range-thumb]:bg-blue-500'
const THUMB_RED = '[&::-webkit-slider-thumb]:bg-red-500 [&::-moz-range-thumb]:bg-red-500'

export interface TemperatureRangeSliderProps {
  lowCelsius: number
  highCelsius: number
  onChange: (range: { lowCelsius: number; highCelsius: number }) => void
  units: Units
  /** Track bounds in Celsius; widened to include the current values. */
  minCelsius?: number
  maxCelsius?: number
}

/**
 * A two-handle temperature range in the user's units: blue below the low handle, green
 * in range, red above the high one. The handles cannot cross. Lifted from the tracker
 * map's history slider, minus its context, so the range modal and anything else can use it.
 */
export const TemperatureRangeSlider = ({
  lowCelsius,
  highCelsius,
  onChange,
  units,
  minCelsius = -40,
  maxCelsius = 40,
}: TemperatureRangeSliderProps) => {
  const suffix = tempUnitSuffix(units)
  const low = Math.round(convertCelsius(lowCelsius, units))
  const high = Math.round(convertCelsius(highCelsius, units))
  const boundsMin = Math.floor(Math.min(convertCelsius(minCelsius, units), low))
  let boundsMax = Math.ceil(Math.max(convertCelsius(maxCelsius, units), high))
  if (boundsMin === boundsMax) boundsMax = boundsMin + 1

  const pct = (v: number) => ((v - boundsMin) / (boundsMax - boundsMin)) * 100
  const trackBg = `linear-gradient(to right, ${BLUE} 0%, ${BLUE} ${pct(low)}%, ${GREEN} ${pct(low)}%, ${GREEN} ${pct(high)}%, ${RED} ${pct(high)}%, ${RED} 100%)`

  const setLow = (value: number) => {
    const clamped = Math.min(Math.max(value, boundsMin), high - 1)
    onChange({ lowCelsius: userToCelsius(clamped, 1, units), highCelsius })
  }
  const setHigh = (value: number) => {
    const clamped = Math.max(Math.min(value, boundsMax), low + 1)
    onChange({ lowCelsius, highCelsius: userToCelsius(clamped, 1, units) })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Acceptable range</span>
        <span className="text-sm font-semibold tabular-nums flex items-center gap-1.5">
          <span style={{ color: BLUE }}>
            {low}
            {suffix}
          </span>
          <span className="text-base-content/40">–</span>
          <span style={{ color: RED }}>
            {high}
            {suffix}
          </span>
        </span>
      </div>
      <div className="relative h-6">
        <div
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full"
          style={{ background: trackBg }}
        />
        <input
          type="range"
          aria-label="Low temperature"
          min={boundsMin}
          max={boundsMax}
          step={1}
          value={low}
          onChange={(e) => setLow(Number(e.target.value))}
          className={`z-20 ${THUMB_BASE} ${THUMB_BLUE}`}
        />
        <input
          type="range"
          aria-label="High temperature"
          min={boundsMin}
          max={boundsMax}
          step={1}
          value={high}
          onChange={(e) => setHigh(Number(e.target.value))}
          className={`z-10 ${THUMB_BASE} ${THUMB_RED}`}
        />
      </div>
      <div className="flex items-center justify-between mt-1 text-[11px] text-base-content/60">
        <span>
          {boundsMin}
          {suffix}
        </span>
        <span>
          {boundsMax}
          {suffix}
        </span>
      </div>
    </div>
  )
}
